import React from "react";
import Typewriter from "typewriter-effect";
import Tilt from "react-parallax-tilt";
import { AiFillGithub } from "react-icons/ai";
import { FaLinkedinIn } from "react-icons/fa";
import { HiOutlineMail, HiOutlineDownload } from "react-icons/hi";
import { profile, stats } from "../data";
import { pick } from "../techIcons";
import { useInView, useCountUp } from "../hooks";
import photo from "../Assets/ahmed-cutout.png";
import cv from "../Assets/Ahmed-Saleem-Khan-CV.pdf";

// Floating badges orbiting the photo, each at a different depth (translateZ) for the 3D effect.
const badges = pick("Go", "Docker", "PostgreSQL", "Kafka", "AWS", "Kubernetes", "Redis");

function Stat({ value, suffix, label, active }) {
  const n = useCountUp(value, active);
  return (
    <div className="stat">
      <strong className="gradient-text">{n}{suffix}</strong>
      <span>{label}</span>
    </div>
  );
}

function Hero() {
  const [ref, seen] = useInView();
  return (
    <section id="home" className="hero">
      <div className="container hero-grid">
        <div className="hero-text">
          <p className="eyebrow fade-in" style={{ "--d": "0ms" }}>
            <span className="pulse-dot" /> Available for remote work
          </p>
          <h1 className="fade-in" style={{ "--d": "120ms" }}>
            Hi, I'm <span className="gradient-text shimmer">{profile.name}</span>
          </h1>
          <h2 className="fade-in" style={{ "--d": "240ms" }}>{profile.title}</h2>
          <div className="typed fade-in" style={{ "--d": "360ms" }}>
            <span className="prompt">&gt;</span>
            <Typewriter
              options={{ strings: profile.roles, autoStart: true, loop: true, deleteSpeed: 40 }}
            />
          </div>
          <p className="lead fade-in" style={{ "--d": "480ms" }}>{profile.summary}</p>
          <div className="hero-tech fade-in" style={{ "--d": "600ms" }}>
            {pick("Go", "gRPC", "Kafka", "RabbitMQ", "PostgreSQL", "Redis", "Docker", "AWS").map(({ name, Icon, color }) => (
              <span key={name} className="tech-pill" style={{ "--c": color }}>
                <Icon /> {name}
              </span>
            ))}
          </div>
          <div className="hero-actions fade-in" style={{ "--d": "720ms" }}>
            <a className="btn btn-primary" href="#contact"><HiOutlineMail /> Get in touch</a>
            <a className="btn btn-ghost" href={cv} download="Ahmed-Saleem-CV.pdf"><HiOutlineDownload /> Download CV</a>
            <a className="icon-btn" href={profile.github} target="_blank" rel="noreferrer" aria-label="GitHub"><AiFillGithub /></a>
            <a className="icon-btn" href={profile.linkedin} target="_blank" rel="noreferrer" aria-label="LinkedIn"><FaLinkedinIn /></a>
          </div>
        </div>

        <div className="hero-photo fade-in" style={{ "--d": "300ms" }}>
          <Tilt
            className="tilt"
            tiltMaxAngleX={18}
            tiltMaxAngleY={18}
            perspective={900}
            scale={1.04}
            glareEnable
            glareMaxOpacity={0.25}
            glareBorderRadius="50%"
            transitionSpeed={1500}
            style={{ transformStyle: "preserve-3d" }}
          >
            <div className="stage">
              <div className="ring ring-a" />
              <div className="ring ring-b" />
              <div className="photo-glow" />
              <img className="photo" src={photo} alt="Ahmed Saleem" />
              <div className="floor" />
              <div className="orbit">
                {badges.map(({ name, Icon, color }, i) => (
                  <span
                    key={name}
                    className="orbit-item"
                    style={{ "--a": `${(360 / badges.length) * i}deg`, "--c": color }}
                  >
                    <span className="orbit-face" title={name}>
                      <Icon />
                    </span>
                  </span>
                ))}
              </div>
              <div className="glass-card gc-1"><strong>Go</strong><span>6+ yrs production</span></div>
              <div className="glass-card gc-2"><strong>44+</strong><span>projects shipped</span></div>
              <div className="glass-card gc-3"><strong>gRPC · Kafka</strong><span>event-driven</span></div>
            </div>
          </Tilt>
        </div>
      </div>

      <div className="container stats" ref={ref}>
        {stats.map((s) => (
          <Stat key={s.label} {...s} active={seen} />
        ))}
      </div>
    </section>
  );
}

export default Hero;
